import { OperadorGrantRow, OperadorPinDialog, useOperadorGrant } from '../../../components/pos/OperadorPinDialog';
import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { createPago } from '../../../services/pagosService';
import { TipoPago, fetchTiposPagoPorPos } from '../../../services/tiposPagoService';
import { fetchSesionesAbiertasPorUsuario, obtenerSesionActivaPorId, SesionPOSLookup, SesionActivaResponse } from '../../../services/pos/sesionPOSService';
import { Afiliacion } from '../../../services/afiliadosService';
import {
    Banknote, CreditCard, X, Smartphone, Wallet,
    CheckCircle, AlertCircle, ExternalLink, Lock, SplitSquareHorizontal,
} from 'lucide-react';

// ─── Design tokens ────────────────────────────────────────────
const C = {
    primary:        '#c1348b',
    primaryDark:    '#8f1f64',
    secondary:      '#61257b',
    secondaryDark:  '#004d7a',
    secondaryLight: '#8b3fa8',
    accent:         '#ffd97c',
    accentDark:     '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
.pm-wrap * { font-family:'DM Sans',sans-serif !important; box-sizing:border-box; }
@keyframes pmFadeUp  { from{opacity:0;transform:translateY(16px) scale(.98);}to{opacity:1;transform:translateY(0) scale(1);} }
@keyframes pmSpin    { to{transform:rotate(360deg);} }
@keyframes pmPulse   { 0%,100%{opacity:1;}50%{opacity:.55;} }
@keyframes pmSlideIn { from{opacity:0;transform:translateX(-8px);}to{opacity:1;transform:translateX(0);} }
.pm-modal { animation:pmFadeUp .32s cubic-bezier(.16,1,.3,1) both; }
.pm-spin   { animation:pmSpin .7s linear infinite; }
.pm-pulse  { animation:pmPulse 1.8s ease-in-out infinite; }
.pm-slide  { animation:pmSlideIn .25s ease both; }
.pm-input { transition:border-color .2s, box-shadow .2s; }
.pm-input:focus { outline:none; border-color:${C.secondary} !important; box-shadow:0 0 0 3px ${C.secondary}18 !important; }
.pm-input:read-only { background:#f8fafc !important; }
.pm-input:disabled { opacity:.55; cursor:not-allowed; }
.pm-scroll::-webkit-scrollbar{width:5px;}
.pm-scroll::-webkit-scrollbar-track{background:transparent;}
.pm-scroll::-webkit-scrollbar-thumb{background:${C.secondary}30;border-radius:10px;}
.pm-scroll::-webkit-scrollbar-thumb:hover{background:${C.secondary}55;}
.pm-tipo-btn { transition:all .22s cubic-bezier(.16,1,.3,1); cursor:pointer; }
.pm-tipo-btn:hover { transform:translateY(-2px); }
.pm-btn-ghost { transition:all .18s; }
.pm-btn-ghost:hover { background:#f1f5f9 !important; }
.pm-btn-primary { transition:all .22s cubic-bezier(.16,1,.3,1); }
.pm-btn-primary:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 8px 24px ${C.secondary}35 !important; }
.pm-btn-primary:disabled { opacity:.5; cursor:not-allowed; transform:none !important; box-shadow:none !important; }
`;

interface PagoAfiliacionModalProps {
    isOpen: boolean;
    onClose: () => void;
    afiliacion: Afiliacion;
    onPagoSuccess: () => void;
}

const PagoAfiliacionModal: React.FC<PagoAfiliacionModalProps> = ({
    isOpen,
    onClose,
    afiliacion,
    onPagoSuccess,
}) => {
    const [tiposPago,            setTiposPago]            = useState<TipoPago[]>([]);
    const [tipoPagoId,           setTipoPagoId]           = useState<number | ''>('');
    const [tipoPagoSeleccionado, setTipoPagoSeleccionado] = useState<TipoPago | null>(null);
    const [efectivoRecibido,     setEfectivoRecibido]     = useState<number>(0);
    const [cambio,               setCambio]               = useState<number>(0);
    const [isLoading,            setIsLoading]            = useState(false);
    const [isProcessingPayment,  setIsProcessingPayment]  = useState(false);
    const [error,                setError]                = useState<string | null>(null);
    const [sesionActiva,         setSesionActiva]         = useState<SesionActivaResponse | null>(null);
    const [verificandoSesion,    setVerificandoSesion]    = useState(false);
    const [showNoSessionPopup,   setShowNoSessionPopup]   = useState(false);
    const [paymentSuccess,       setPaymentSuccess]       = useState(false);
    const [montoAPagar,          setMontoAPagar]          = useState<number>(0);
    const [ultimoCambio,         setUltimoCambio]         = useState<number>(0);

    const [sesionesDisponibles, setSesionesDisponibles] = useState<SesionPOSLookup[]>([]);
    const operador = useOperadorGrant(sesionActiva?.id ?? null);

    const montoTotal = Number(afiliacion.monto_total || 0);
    const montoPagado = Number(afiliacion.monto_pagado || 0);
    const saldoPendiente = Math.max(0, montoTotal - montoPagado);

    const redondear = useCallback((valor: number) =>
        Math.round((valor + Number.EPSILON) * 100) / 100, []);

    // Cargar tipos de pago de la sesión POS
    const cargarTiposPago = useCallback(async (sesion: SesionActivaResponse) => {
        setIsLoading(true);
        try {
            const tipos = await fetchTiposPagoPorPos(sesion.punto_de_venta);
            setTiposPago(tipos);
            if (tipos.length > 0) {
                const primerTipo = tipos[0];
                setTipoPagoId(primerTipo.id);
                setTipoPagoSeleccionado(primerTipo);
                const saldo = redondear(saldoPendiente > 0 ? saldoPendiente : montoTotal);
                setMontoAPagar(saldo);
                setEfectivoRecibido(saldo);
            }
        } catch (err) {
            setError('Error al cargar los tipos de pago');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [saldoPendiente, montoTotal, redondear]);

    // Verificar sesión POS activa
    const verificarSesionActiva = useCallback(async () => {
        setVerificandoSesion(true);
        try {
            const sesiones = await fetchSesionesAbiertasPorUsuario();
            setSesionesDisponibles(sesiones);
            setSesionActiva(null);
            operador.reset();
            setShowNoSessionPopup(sesiones.length === 0);
        } catch {
            setSesionActiva(null);
            setShowNoSessionPopup(true);
        } finally {
            setVerificandoSesion(false);
        }
    }, [cargarTiposPago]);

    useEffect(() => {
        if (!isOpen) {
            setIsProcessingPayment(false);
            setPaymentSuccess(false);
            setError(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            verificarSesionActiva();
            // Obtener último cambio si hay pagos previos
            if (afiliacion.pagos && Array.isArray(afiliacion.pagos) && afiliacion.pagos.length > 0) {
                const ult = afiliacion.pagos[0];
                setUltimoCambio(Number(ult.cambio) || 0);
            } else {
                setUltimoCambio(0);
            }
        }
    }, [isOpen, afiliacion, verificarSesionActiva]);

    // Cálculo dinámico de montos y cambio
    useEffect(() => {
        const saldo = redondear(saldoPendiente);
        if (tipoPagoSeleccionado?.configuracion_detail) {
            const config = tipoPagoSeleccionado.configuracion_detail;
            if (!config.permite_monto_personalizado) {
                setMontoAPagar(saldo);
            }
            if (config.requiere_cambio) {
                setCambio(redondear(efectivoRecibido > montoAPagar ? efectivoRecibido - montoAPagar : 0));
            } else if (config.permite_monto_personalizado && !config.afecta_caja && efectivoRecibido > montoAPagar) {
                setCambio(redondear(efectivoRecibido - montoAPagar));
            } else {
                setCambio(0);
                if (!config.afecta_caja && !config.permite_monto_personalizado) {
                    setEfectivoRecibido(redondear(montoAPagar));
                }
            }
        } else {
            setCambio(redondear(efectivoRecibido > montoAPagar ? efectivoRecibido - montoAPagar : 0));
        }
    }, [efectivoRecibido, saldoPendiente, montoAPagar, tipoPagoSeleccionado, redondear]);

    const handleTipoPagoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = Number(e.target.value);
        setTipoPagoId(id);
        const tipo = tiposPago.find(t => t.id === id);
        setTipoPagoSeleccionado(tipo || null);
        const saldo = redondear(saldoPendiente > 0 ? saldoPendiente : montoTotal);
        setMontoAPagar(saldo);
        setEfectivoRecibido(saldo);
    };

    const getIconoTipoPago = (tipo: string) => {
        switch (tipo) {
            case 'efectivo': return <Banknote size={16}/>;
            case 'banco':    return <CreditCard size={16}/>;
            case 'otro':     return <Smartphone size={16}/>;
            default:         return <Wallet size={16}/>;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsProcessingPayment(true);
        setPaymentSuccess(false);

        if (!sesionActiva) {
            setError('No hay sesión POS activa');
            setIsProcessingPayment(false);
            return;
        }
        if (!tipoPagoId || !tipoPagoSeleccionado) {
            setError('Por favor seleccione un tipo de pago');
            setIsProcessingPayment(false);
            return;
        }

        const config = tipoPagoSeleccionado.configuracion_detail;
        const efectivoRedondeado = redondear(efectivoRecibido);
        const montoFinal = redondear(montoAPagar);

        if (config) {
            if (config.requiere_cambio) {
                if (efectivoRedondeado <= 0) {
                    setError('El efectivo recibido debe ser mayor a 0');
                    setIsProcessingPayment(false);
                    return;
                }
                if (efectivoRedondeado < montoFinal) {
                    setError('El efectivo recibido no puede ser menor al monto a pagar');
                    setIsProcessingPayment(false);
                    return;
                }
            }
            if (config.permite_monto_personalizado && montoFinal <= 0) {
                setError('El monto a pagar debe ser mayor a 0');
                setIsProcessingPayment(false);
                return;
            }
            if (config.permite_monto_personalizado && montoFinal > redondear(saldoPendiente)) {
                setError(`El monto no puede superar el saldo pendiente de ${redondear(saldoPendiente).toFixed(2)} Bs.`);
                setIsProcessingPayment(false);
                return;
            }
        }
        if (montoFinal <= 0) {
            setError('Debe ingresar un monto mayor a 0');
            setIsProcessingPayment(false);
            return;
        }

        try {
            const datosPago: any = {
                afiliacion: afiliacion.id,
                tipo_pago: tipoPagoId,
                monto: montoFinal,
                sesion_pos: sesionActiva.id
            };

            if (config?.requiere_cambio) {
                datosPago.efectivo_recibido = efectivoRedondeado;
            } else if (config?.permite_monto_personalizado && !config?.afecta_caja && efectivoRedondeado > montoFinal) {
                datosPago.efectivo_recibido = efectivoRedondeado;
            } else if (config?.afecta_caja) {
                datosPago.efectivo_recibido = montoFinal;
            }

            await createPago(datosPago, operador.token);
            setPaymentSuccess(true);
            await new Promise(r => setTimeout(r, 300));
            setIsProcessingPayment(false);
            onPagoSuccess();
            onClose();
        } catch (err: any) {
            setIsProcessingPayment(false);
            setPaymentSuccess(false);
            if (err.response?.data?.error) setError(err.response.data.error);
            else if (err.response?.data?.detail) setError(err.response.data.detail);
            else if (err.response?.data) setError(Object.values(err.response.data).flat().join(' '));
            else setError('Error al registrar el pago');
        }
    };

    const handleIrAPOS = () => { onClose(); window.open('/pos-list', '_blank'); };
    const handleAbrirSesionPOS = () => { onClose(); window.open('/pos-list', '_blank'); };
    const handleCloseNoSessionPopup = () => { setShowNoSessionPopup(false); onClose(); };

    if (!isOpen) return null;

    const config = tipoPagoSeleccionado?.configuracion_detail;
    const permitePersonalizado = config?.permite_monto_personalizado ?? false;
    const esPagoDigitalConCambio = permitePersonalizado && !config?.afecta_caja && efectivoRecibido > montoAPagar;

    const configColor = (afecta: boolean, requiere: boolean) => {
        if (!afecta)            return { bg: '#7c3aed', light: '#ede9fe', border: '#c4b5fd' };
        if (afecta && requiere) return { bg: C.secondary, light: `${C.secondary}12`, border: `${C.secondary}30` };
        return { bg: C.secondaryLight, light: `${C.secondaryLight}12`, border: `${C.secondaryLight}30` };
    };

    const inputBase: React.CSSProperties = {
        width: '100%', padding: '10px 14px', borderRadius: '10px',
        border: '1.5px solid #e2e8f0', fontSize: '.88rem', color: '#0f172a',
        background: '#fff', fontFamily: "'DM Sans',sans-serif", fontWeight: '500',
    };

    const modalContent = (
        <div className="pm-wrap" style={{ position:'fixed',inset:0,zIndex:99999,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px' }}>
            <style>{css}</style>
            {!verificandoSesion && !sesionActiva && sesionesDisponibles.length > 0 && <div style={{ position: 'relative', zIndex: 1, background: '#fff', borderRadius: 16, padding: 24 }}>
                <h3>Selecciona la caja para cobrar la domiciliación</h3>
                <select aria-label="Caja para el cobro" defaultValue="" onChange={async e => {
                    const id = Number(e.target.value);
                    if (!sesionesDisponibles.some(s => s.id === id)) return;
                    try { const sesion = await obtenerSesionActivaPorId(id); setSesionActiva(sesion); await cargarTiposPago(sesion); }
                    catch { setError('No se pudo abrir la caja seleccionada.'); }
                }}><option value="" disabled>Seleccionar caja</option>{sesionesDisponibles.map(s => <option key={s.id} value={s.id}>{s.codigo} · {s.punto_de_venta_nombre} · {s.sucursal_nombre}</option>)}</select>
                <button type="button" onClick={onClose}>Cancelar</button>
            </div>}
            <OperadorPinDialog open={operador.dialogOpen} sesionId={sesionActiva?.id ?? null} onClose={operador.closeDialog} onGrant={operador.install} />
            <div style={{ position:'fixed',inset:0,background:'rgba(15,23,42,.55)',backdropFilter:'blur(4px)' }} onClick={isProcessingPayment ? undefined : onClose}/>

            {/* ══ VERIFICANDO ══ */}
            {verificandoSesion && (
                <div className="pm-modal" style={{ position:'relative',background:'#fff',borderRadius:'22px',padding:'48px 32px',textAlign:'center',boxShadow:'0 24px 80px rgba(0,0,0,.18)',maxWidth:'380px',width:'100%' }}>
                    <div style={{ width:'52px',height:'52px',border:`3px solid ${C.secondary}20`,borderTopColor:C.secondary,borderRadius:'50%',margin:'0 auto 16px' }} className="pm-spin"/>
                    <p style={{ fontSize:'.92rem',color:'#64748b',fontWeight:'600' }}>Verificando sesión POS activa…</p>
                </div>
            )}

            {/* ══ SIN SESIÓN ══ */}
            {!verificandoSesion && showNoSessionPopup && (
                <div className="pm-modal" style={{ position:'relative',background:'#fff',borderRadius:'22px',maxWidth:'520px',width:'100%',boxShadow:'0 24px 80px rgba(0,0,0,.18)',overflow:'hidden' }}>
                    <div style={{ height:'4px',background:`linear-gradient(90deg,${C.primary},${C.secondary},${C.accentDark},${C.secondaryLight})` }}/>
                    <div style={{ padding:'24px 26px 20px',borderBottom:'1.5px solid #f1f5f9',display:'flex',alignItems:'center',gap:'14px' }}>
                        <div style={{ width:'48px',height:'48px',borderRadius:'14px',background:`${C.primary}10`,border:`1.5px solid ${C.primary}20`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                            <Lock size={22} color={C.primary}/>
                        </div>
                        <div>
                            <h3 style={{ fontSize:'1rem',fontWeight:'900',color:'#0f172a',letterSpacing:'-.015em' }}>Sesión POS Requerida</h3>
                            <p style={{ fontSize:'.8rem',color:'#64748b',marginTop:'3px' }}>Para registrar pagos, necesita una sesión activa.</p>
                        </div>
                    </div>
                    {/* Footer */}
                    <div style={{ padding:'18px 26px',borderTop:'1.5px solid #f1f5f9',background:'#fafafa',display:'flex',flexDirection:'column',gap:'10px' }}>
                        <div style={{ display:'flex',gap:'10px' }}>
                            <button className="pm-btn-ghost" onClick={handleCloseNoSessionPopup}
                                style={{ flex:1,padding:'10px',borderRadius:'11px',border:'1.5px solid #e2e8f0',background:'#fff',fontSize:'.85rem',fontWeight:'700',color:'#64748b',cursor:'pointer' }}>
                                Cancelar
                            </button>
                            <button className="pm-btn-primary" onClick={handleAbrirSesionPOS}
                                style={{ flex:1,padding:'10px',borderRadius:'11px',border:'none',background:`linear-gradient(135deg,${C.secondary},${C.secondaryDark})`,color:'#fff',fontSize:'.85rem',fontWeight:'800',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'7px',boxShadow:`0 4px 16px ${C.secondary}30` }}>
                                <Banknote size={14}/> Abrir Sesión POS <ExternalLink size={12} style={{ opacity:.7 }}/>
                            </button>
                        </div>
                        <button className="pm-btn-ghost" onClick={handleIrAPOS}
                            style={{ width:'100%',padding:'9px',borderRadius:'11px',border:`1.5px solid ${C.secondary}20`,background:`${C.secondary}06`,fontSize:'.82rem',fontWeight:'700',color:C.secondary,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'6px' }}>
                            <CreditCard size={13}/> Ver estado actual del POS
                        </button>
                    </div>
                </div>
            )}

            {/* ══ MODAL PRINCIPAL ══ */}
            {!verificandoSesion && !showNoSessionPopup && (
                <div className="pm-modal" style={{ position:'relative',background:'#fff',borderRadius:'22px',maxWidth:'680px',width:'100%',boxShadow:'0 24px 80px rgba(0,0,0,.18)',overflow:'hidden',maxHeight:'92vh',display:'flex',flexDirection:'column' }}>
                    <div style={{ height:'4px',background:`linear-gradient(90deg,${C.secondary},${C.secondaryLight},${C.accentDark},${C.primary})`,flexShrink:0 }}/>

                    {/* HEADER */}
                    <div style={{ padding:'22px 26px 18px',borderBottom:'1.5px solid #f1f5f9',flexShrink:0 }}>
                        <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:'12px' }}>
                            <div>
                                <div style={{ display:'flex',alignItems:'center',gap:'10px',marginBottom:'6px' }}>
                                    <div style={{ width:'36px',height:'36px',borderRadius:'11px',background:`linear-gradient(135deg,${C.secondary},${C.secondaryDark})`,display:'flex',alignItems:'center',justifyContent:'center',boxShadow:`0 3px 12px ${C.secondary}30` }}>
                                        <CreditCard size={17} color="#fff"/>
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize:'1rem',fontWeight:'900',color:'#0f172a',letterSpacing:'-.02em' }}>
                                            {isProcessingPayment ? 'Registrando Pago…' : 'Registrar Pago'}
                                        </h3>
                                        <p style={{ fontSize:'.75rem',color:'#94a3b8',fontWeight:'500' }}>
                                            {isProcessingPayment ? 'Por favor espere…' : 'Complete la información del pago'}
                                        </p>
                                    </div>
                                </div>
                                {sesionActiva && (
                                    <div className="pm-slide" style={{ display:'inline-flex',alignItems:'center',gap:'6px',background:`${C.secondaryLight}12`,border:`1.5px solid ${C.secondaryLight}30`,borderRadius:'100px',padding:'4px 12px' }}>
                                        <span style={{ width:'7px',height:'7px',borderRadius:'50%',background:C.secondaryLight,display:'inline-block' }}/>
                                        <span style={{ fontSize:'.7rem',fontWeight:'700',color:'#065f46' }}>Sesión POS activa</span>
                                        <span style={{ fontSize:'.68rem',color:'#94a3b8',fontWeight:'500' }}>· {sesionActiva.punto_de_venta_nombre} · {sesionActiva.codigo}</span>
                                    </div>
                                )}
                            </div>
                            <button onClick={isProcessingPayment ? undefined : onClose} disabled={isProcessingPayment}
                                style={{ width:'32px',height:'32px',borderRadius:'9px',border:'1.5px solid #f1f5f9',background:'#f8fafc',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'background .15s' }}>
                                <X size={15} color="#64748b"/>
                            </button>
                        </div>
                    </div>

                    {/* BODY */}
                    <form onSubmit={handleSubmit} style={{ display:'flex',flexDirection:'column',flex:1,minHeight:0 }}>
                        <div className="pm-scroll" style={{ padding:'22px 26px',overflowY:'auto',flex:1,display:'flex',flexDirection:'column',gap:'20px' }}>

                            {/* Procesando */}
                            {isProcessingPayment && (
                                <div className="pm-pulse" style={{ background:`${C.secondary}08`,border:`1.5px solid ${C.secondary}25`,borderRadius:'14px',padding:'14px 16px',display:'flex',alignItems:'center',gap:'12px' }}>
                                    <div className="pm-spin" style={{ width:'20px',height:'20px',border:`2.5px solid ${C.secondary}25`,borderTopColor:C.secondary,borderRadius:'50%',flexShrink:0 }}/>
                                    <div>
                                        <p style={{ fontSize:'.85rem',fontWeight:'700',color:C.secondaryDark }}>
                                            {paymentSuccess ? 'Pago registrado exitosamente' : 'Procesando pago…'}
                                        </p>
                                        <p style={{ fontSize:'.75rem',color:'#64748b',marginTop:'2px' }}>
                                            {paymentSuccess ? 'Actualizando información…' : 'No cierre esta ventana ni presione el botón nuevamente'}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Error general */}
                            {error && !isProcessingPayment && (
                                <div style={{ background:`${C.primary}07`,borderLeft:`4px solid ${C.primary}`,borderRadius:'0 12px 12px 0',padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px' }}>
                                    <div style={{ display:'flex',alignItems:'center',gap:'10px' }}>
                                        <AlertCircle size={15} color={C.primary} style={{ flexShrink:0 }}/>
                                        <p style={{ fontSize:'.83rem',fontWeight:'600',color:C.primaryDark }}>{error}</p>
                                    </div>
                                    <button type="button" onClick={()=>setError(null)} style={{ background:'none',border:'none',cursor:'pointer',color:'#94a3b8',flexShrink:0 }}><X size={13}/></button>
                                </div>
                            )}

                            {/* ── RESUMEN DE MONTOS (4 CARDS) ── */}
                            <div style={{ display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px' }}>
                                {[
                                    { label:'Total Plan',    val:montoTotal,              icon:<Banknote size={15}/>,  color:C.secondary,       bg:`${C.secondary}10` },
                                    { label:'Pagado',        val:montoPagado,            icon:<CheckCircle size={15}/>,color:C.secondaryLight, bg:`${C.secondaryLight}12` },
                                    { label:'Último Cambio', val:ultimoCambio,           icon:<Wallet size={15}/>,    color:'#64748b',         bg:'#f1f5f9' },
                                    { label:'Pendiente',     val:Math.max(0,saldoPendiente), icon:<Banknote size={15}/>, color:saldoPendiente>0?C.accentDark:'#64748b', bg:saldoPendiente>0?`${C.accentDark}12`:'#f1f5f9' },
                                ].map(item => (
                                    <div key={item.label} style={{ background:'#fff',border:'1.5px solid #f1f5f9',borderRadius:'16px',padding:'16px 14px',boxShadow:'0 1px 8px rgba(0,0,0,.04)' }}>
                                        <div style={{ display:'flex',alignItems:'center',gap:'8px',marginBottom:'8px' }}>
                                            <div style={{ width:'28px',height:'28px',borderRadius:'8px',background:item.bg,display:'flex',alignItems:'center',justifyContent:'center',color:item.color,flexShrink:0 }}>{item.icon}</div>
                                            <span style={{ fontSize:'.65rem',fontWeight:'700',color:'#94a3b8',textTransform:'uppercase',letterSpacing:'.07em' }}>{item.label}</span>
                                        </div>
                                        <div style={{ fontSize:'1.05rem',fontWeight:'900',color:'#0f172a',letterSpacing:'-.02em' }}>
                                            {item.val.toFixed(2)} <span style={{ fontSize:'.72rem',fontWeight:'500',color:'#94a3b8' }}>Bs.</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* ── TIPO DE PAGO ── */}
                            <div>
                                <label style={{ display:'block',fontSize:'.8rem',fontWeight:'700',color:'#374151',marginBottom:'8px' }}>
                                    Tipo de Pago <span style={{ color:C.primary }}>*</span>
                                </label>
                                <OperadorGrantRow grant={operador.grant} onIdentify={operador.openDialog} disabled={isProcessingPayment} />
                                <select value={tipoPagoId} onChange={handleTipoPagoChange}
                                    disabled={isLoading || !sesionActiva || isProcessingPayment}
                                    className="pm-input"
                                    style={{ ...inputBase, appearance:'none', backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat:'no-repeat', backgroundPosition:'right 14px center', paddingRight:'36px' }}>
                                    {tiposPago.map(tipo => <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>)}
                                </select>

                                {/* Badge config */}
                                {config && (() => {
                                    const cc = configColor(config.afecta_caja, config.requiere_cambio);
                                    return (
                                        <div style={{ marginTop:'12px',background:cc.light,border:`1.5px solid ${cc.border}`,borderRadius:'14px',padding:'14px 16px',display:'flex',alignItems:'flex-start',gap:'12px' }}>
                                            <div style={{ width:'34px',height:'34px',borderRadius:'10px',background:cc.light,border:`1.5px solid ${cc.border}`,display:'flex',alignItems:'center',justifyContent:'center',color:cc.bg,flexShrink:0 }}>
                                                {getIconoTipoPago(config.tipo)}
                                            </div>
                                            <div style={{ flex:1 }}>
                                                <p style={{ fontSize:'.85rem',fontWeight:'800',color:'#0f172a',marginBottom:'8px' }}>{config.nombre}</p>
                                                <div style={{ display:'flex',flexWrap:'wrap',gap:'6px' }}>
                                                    <span style={{ display:'inline-flex',alignItems:'center',gap:'3px',background:config.afecta_caja?`${C.secondaryLight}20`:'#ede9fe',border:`1px solid ${config.afecta_caja?C.secondaryLight:'#c4b5fd'}`,borderRadius:'100px',padding:'3px 9px',fontSize:'.65rem',fontWeight:'700',color:config.afecta_caja?'#065f46':'#6d28d9' }}>
                                                        {config.afecta_caja ? 'Afecta a caja' : 'No afecta a caja'}
                                                    </span>
                                                    {config.requiere_cambio && <span style={{ display:'inline-flex',alignItems:'center',background:`${C.secondary}15`,border:`1px solid ${C.secondary}30`,borderRadius:'100px',padding:'3px 9px',fontSize:'.65rem',fontWeight:'700',color:C.secondaryDark }}>Requiere cambio</span>}
                                                    {config.permite_monto_personalizado && <span style={{ display:'inline-flex',alignItems:'center',gap:'3px',background:'#ede9fe',border:'1px solid #c4b5fd',borderRadius:'100px',padding:'3px 9px',fontSize:'.65rem',fontWeight:'700',color:'#6d28d9' }}><SplitSquareHorizontal size={10}/> Pago parcial</span>}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* ── MONTO A PAGAR ── */}
                            <div>
                                <label style={{ display:'flex',alignItems:'center',gap:'7px',fontSize:'.8rem',fontWeight:'700',color:'#374151',marginBottom:'8px' }}>
                                    Monto a Pagar <span style={{ color:C.primary }}>*</span>
                                    {permitePersonalizado && <span style={{ fontSize:'.65rem',fontWeight:'700',background:'#ede9fe',color:'#6d28d9',borderRadius:'100px',padding:'2px 8px' }}>Monto libre</span>}
                                </label>
                                <input type="number" min="0.01" step="0.01" max={redondear(saldoPendiente)}
                                    value={montoAPagar}
                                    onChange={e => {
                                        if (permitePersonalizado) {
                                            const val = parseFloat(e.target.value);
                                            const nm = isNaN(val) ? 0 : redondear(Math.min(val, saldoPendiente));
                                            setMontoAPagar(nm);
                                            if (!config?.requiere_cambio && config?.afecta_caja) setEfectivoRecibido(nm);
                                        }
                                    }}
                                    readOnly={!permitePersonalizado || isProcessingPayment}
                                    disabled={isProcessingPayment}
                                    className="pm-input"
                                    style={{ ...inputBase, borderColor: permitePersonalizado ? '#c4b5fd' : '#e2e8f0', background: permitePersonalizado ? '#fff' : '#f8fafc', fontWeight:'700', fontSize:'1rem' }}
                                />
                                {permitePersonalizado && <p style={{ fontSize:'.72rem',color:'#7c3aed',fontWeight:'500',marginTop:'5px' }}>Puede ingresar un monto parcial (máx: {redondear(saldoPendiente).toFixed(2)} Bs.)</p>}
                            </div>

                            {/* ── EFECTIVO RECIBIDO ── */}
                            {(config?.afecta_caja || esPagoDigitalConCambio || (permitePersonalizado && !config?.afecta_caja)) && (
                                <div>
                                    <label style={{ display:'flex',alignItems:'center',gap:'7px',fontSize:'.8rem',fontWeight:'700',color:'#374151',marginBottom:'8px' }}>
                                        {config?.requiere_cambio || esPagoDigitalConCambio ? 'Monto Recibido *' : 'Monto a Pagar'}
                                    </label>
                                    <input type="number" min="0.01" step="0.01"
                                        value={efectivoRecibido}
                                        onChange={e => {
                                            const val = parseFloat(e.target.value);
                                            setEfectivoRecibido(isNaN(val) ? 0 : redondear(val));
                                        }}
                                        disabled={(!config?.requiere_cambio && !esPagoDigitalConCambio) || isProcessingPayment}
                                        className="pm-input"
                                        style={{ ...inputBase, fontWeight:'700', fontSize:'1rem' }}
                                    />
                                    {config?.requiere_cambio && <p style={{ fontSize:'.72rem',color:'#64748b',fontWeight:'500',marginTop:'5px' }}>Ingrese el monto recibido para calcular el cambio</p>}
                                </div>
                            )}

                            {/* ── CAMBIO A ENTREGAR ── */}
                            {(config?.requiere_cambio || esPagoDigitalConCambio) && (
                                <div>
                                    <label style={{ display:'block',fontSize:'.8rem',fontWeight:'700',color:'#374151',marginBottom:'8px' }}>
                                        Cambio a Entregar
                                    </label>
                                    <input type="text" readOnly
                                        value={`${cambio.toFixed(2)} Bs.`}
                                        className="pm-input"
                                        style={{ ...inputBase, background:'#f8fafc', color:cambio>0?'#059669':'#64748b', fontWeight:'700', fontSize:'1rem' }}
                                    />
                                </div>
                            )}

                        </div>

                        {/* FOOTER */}
                        <div style={{ padding:'16px 26px 20px',borderTop:'1.5px solid #f1f5f9',background:'#fafafa',display:'flex',alignItems:'center',justifyContent:'flex-end',gap:'10px',flexShrink:0 }}>
                            <button type="button" onClick={onClose} disabled={isProcessingPayment} className="pm-btn-ghost"
                                style={{ padding:'10px 20px',borderRadius:'11px',border:'1.5px solid #e2e8f0',background:'#fff',fontSize:'.85rem',fontWeight:'700',color:'#64748b',cursor:'pointer' }}>
                                Cancelar
                            </button>
                            <button type="submit" disabled={isProcessingPayment || !sesionActiva || !tipoPagoId || !operador.token} className="pm-btn-primary"
                                style={{ padding:'10px 24px',borderRadius:'11px',border:'none',background:`linear-gradient(135deg,${C.secondary},${C.secondaryDark})`,color:'#fff',fontSize:'.88rem',fontWeight:'800',cursor:'pointer',display:'inline-flex',alignItems:'center',gap:'8px',boxShadow:`0 4px 16px ${C.secondary}35` }}>
                                <CreditCard size={15}/> {isProcessingPayment ? 'Registrando…' : 'Registrar Pago'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );

    return ReactDOM.createPortal(modalContent, document.body);
};

export default PagoAfiliacionModal;

